import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { IPatient } from 'src/app/models/patient.model';
import { ICountriesDDI } from 'src/app/models/countries-ddi.model';
import { countriesDdi } from 'src/app/models/countries_ddi';
import { PatientsService } from 'src/app/services/patients.service';
import { ModulesService } from 'src/app/services/modules.service';
import { SearchService } from '../../services/search.service';
import { ExportService } from '../../services/export.service';
import { DocumentsService } from 'src/app/services/documents.service';
import { AuthService } from 'src/app/services/auth.service';
import { ContextService } from 'src/app/services/context.service';
import { 
  faAngleDoubleLeft, 
  faAngleDoubleRight, 
  faAngleLeft, 
  faAngleRight, 
  faSort, 
  faSortDown, 
  faSortUp, 
  faTimes 
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-patients-v2',
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.sass']
})
export class PatientsV2Component implements OnInit, OnDestroy {
  loading = false;
  exporting = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  sortBy = 'ASC';
  orderBy = 'fullName';
  searchTerm: string | undefined = undefined;
  patients: IPatient[] = [];
  countriesDdi = countriesDdi;
  
  private searchSubscription?: Subscription;
  private exportSubscription?: Subscription;

  // Delete modal
  showConfirmDeletePopup = false;
  patientToDelete = '';

  // Pending contracts warning modal
  showPendingContractsPopup = false;
  pendingContracts = 0;
  agreeChecked = false;

  // Expor Math para o template
  Math = Math;

  // Label dinâmico para paginação
  moduleLabel = 'pacientes';

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 1000, label: 'Todos' }
  ];

  // Icons
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faTimes = faTimes;

  // Módulo dinâmico
  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;
  displayName = 'Pacientes';
  moduleNameSingular = 'paciente';
  baseRoute = '/main/v2/patients';
  private moduleSubscription?: Subscription;

  // Sharing
  sharingActive = false;
  activeCnpj = '';

  constructor(
    private patientsService: PatientsService,
    private router: Router,
    private searchService: SearchService,
    private exportService: ExportService,
    public modulesService: ModulesService,
    private documentsService: DocumentsService,
    private authService: AuthService,
    private contextService: ContextService
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    this.loading = true;
    
    // Limpa o termo de busca ao entrar na página
    this.searchService.setSearchTerm('');

    // Detecta se compartilhamento de pacientes está ativo
    const clinic = this.contextService.getActiveClinicSnapshot();
    this.sharingActive = !!clinic?.sharePatients;
    this.activeCnpj = clinic?.cnpj || '';

    // Carrega os pacientes imediatamente
    this.getPatients(0, this.pageSize);

    // Escuta mudanças no termo de pesquisa do header
    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      // Só recarrega se o termo realmente mudou
      if (this.searchTerm !== (term || undefined)) {
        this.searchTerm = term || undefined;
        this.getPatients(0, this.pageSize);
      }
    });

    // Escuta o trigger de exportação do header
    this.exportSubscription = this.exportService.exportTrigger$.subscribe(() => {
      this.exportPatients();
    });

    // Verifica contratos pendentes de assinatura do paciente
    this.checkPendingContracts();

    // Ajusta título/rotas com base no módulo selecionado
    this.moduleSubscription = this.viewModules$.subscribe(mod => {
      const name = (mod?.moduleName || '').toString().toUpperCase();
      if (name === 'PACIENTES') {
        this.displayName = 'Pacientes';
        this.moduleNameSingular = 'paciente';
        this.moduleLabel = 'pacientes';
        this.baseRoute = '/main/v2/patients';
      } else {
        // Para qualquer outro módulo, usa a rota dinâmica
        this.displayName = mod?.moduleNamePascalCase || name;
        this.moduleNameSingular = mod?.moduleNameInSingular || 'registro';
        this.moduleLabel = mod?.moduleName?.toLowerCase() || 'registros';
        this.baseRoute = `/main/v2/modules/${name.toLowerCase()}`;
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.exportSubscription?.unsubscribe();
    this.moduleSubscription?.unsubscribe();
  }

  toggleSort(column: string): void {
    if (this.orderBy === column) {
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getPatients(0, this.pageSize);
  }

  getPatients(pageNumber: number, pageSize: number): void {
    this.loading = true;
    this.currentPage = pageNumber;
    this.patientsService.getPatients(pageNumber, pageSize, this.sortBy, this.orderBy, this.searchTerm || '', this.modulesService.moduleId)
      .subscribe({
        next: (response: any) => {
          this.patients = response.data;
          if (response.page_info) {
            this.currentPage = response.page_info.current_page;
            this.totalPages = response.page_info.total_pages;
            this.totalElements = response.page_info.total_elements;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar pacientes:', error);
          this.loading = false;
        }
      });
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getPatients(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getPatients(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getPatients(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getPatients(this.totalPages - 1, this.pageSize);
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.getPatients(0, this.pageSize);
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getPatients(+page - 1, this.pageSize);
    }
  }

  edit(id?: string): void {
    this.editEntry(id);
  }

  create(): void {
    this.createEntry();
  }

  createEntry(): void {
    // Navega usando a rota base definida pelo módulo selecionado
    this.router.navigateByUrl(`${this.baseRoute}/create`);
  }

  editEntry(id?: string): void {
    // Navega usando a rota base definida pelo módulo selecionado
    const target = id ? `${this.baseRoute}/${id}/edit` : `${this.baseRoute}/${id}/edit`;
    this.router.navigateByUrl(target);
  }

  showDeleteModal(id?: string): void {
    this.showConfirmDeletePopup = true;
    this.patientToDelete = id || '';
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.patientToDelete = '';
  }

  onDelete(): void {
    // Implementar quando o endpoint de delete estiver disponível
    this.closeDeleteModal();
  }

  getSelectedPhoneCountryFromDto(patient: IPatient): ICountriesDDI {
    return this.countriesDdi.find(c => c.pais === patient.phone_country_code) || this.getDefaultCountryDdiObject();
  }

  getDefaultCountryDdiObject(): ICountriesDDI {
    return {
      "pais": "Brasil",
      "img": "https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Flag_of_Brazil.svg/22px-Flag_of_Brazil.svg.png",
      "ddi": "+55"
    } as ICountriesDDI;
  }

  formatRg(value: string | undefined): string {
    if (!value) return '-';
    return value.replace(/[^a-zA-Z0-9]/g, "");
  }

  isFromOtherClinic(patient: IPatient): boolean {
    return this.sharingActive && !!patient.origin_clinic_cnpj && patient.origin_clinic_cnpj !== this.activeCnpj;
  }

  exportPatients(): void {
    if (this.exporting) return;
    this.exporting = true;
    this.exportService.setExporting(true);
    
    const search = this.searchTerm || '';
    this.patientsService.exportPatientsCsv(search, this.orderBy, this.sortBy, this.modulesService.moduleId).subscribe({
      next: (resp) => {
        const blob = resp.body as Blob;
        const cd = resp.headers.get('Content-Disposition') || '';
        const match = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
        let filename = match?.[1] || `${this.modulesService.moduleName.toLowerCase() || 'pacientes'}.csv`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        this.exporting = false;
        this.exportService.setExporting(false);
      },
      error: () => {
        this.exporting = false;
        this.exportService.setExporting(false);
        alert('Erro ao exportar a lista de pacientes.');
      }
    });
  }

  // ========== Pending Contracts Warning ==========

  private readonly PENDING_CONTRACTS_STORAGE_KEY = 'pendingContractsWarningDismissed';
  private readonly TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

  /**
   * Verifica se o alerta de contratos pendentes deve ser mostrado para o usuário atual.
   * O alerta não é mostrado se o usuário já o dispensou nas últimas 24 horas.
   */
  private shouldShowPendingContractsWarning(): boolean {

    const currentUserId = this.authService.getUserId();
    if (!currentUserId) return true;

    try {
      const storedData = localStorage.getItem(this.PENDING_CONTRACTS_STORAGE_KEY);
      if (!storedData) return true;

      // Objeto que mapeia userId -> timestamp
      const dismissedUsers: { [userId: string]: number } = JSON.parse(storedData);
      
      // Verifica se o usuário atual já dispensou o alerta
      const userTimestamp = dismissedUsers[currentUserId];
      if (!userTimestamp) return true;

      // Verificar se já passaram 24 horas
      const now = Date.now();
      const elapsed = now - userTimestamp;
      
      return elapsed >= this.TWENTY_FOUR_HOURS_MS;
    } catch {
      return true;
    }
  }

  /**
   * Salva no localStorage que o usuário atual dispensou o alerta.
   * Mantém os dados de outros usuários que já dispensaram anteriormente.
   */
  private savePendingContractsWarningDismissed(): void {
    const currentUserId = this.authService.getUserId();
    if (!currentUserId) return;

    try {
      const storedData = localStorage.getItem(this.PENDING_CONTRACTS_STORAGE_KEY);
      // Carrega os dados existentes ou cria um objeto vazio
      const dismissedUsers: { [userId: string]: number } = storedData ? JSON.parse(storedData) : {};
      
      // Adiciona/atualiza o timestamp do usuário atual
      dismissedUsers[currentUserId] = Date.now();
      
      // Limpa entradas antigas (mais de 24h) para não acumular lixo
      const now = Date.now();
      for (const userId in dismissedUsers) {
        if (now - dismissedUsers[userId] >= this.TWENTY_FOUR_HOURS_MS) {
          delete dismissedUsers[userId];
        }
      }
      
      localStorage.setItem(this.PENDING_CONTRACTS_STORAGE_KEY, JSON.stringify(dismissedUsers));
    } catch {
      // Em caso de erro, tenta salvar só o usuário atual
      const data = { [currentUserId]: Date.now() };
      localStorage.setItem(this.PENDING_CONTRACTS_STORAGE_KEY, JSON.stringify(data));
    }
  }

  /**
   * Verifica contratos pendentes de assinatura do paciente.
   * Busca contratos do início dos tempos até 3 dias atrás usando o filtro PENDING_PATIENT_SIGNATURE.
   * Só mostra o alerta se o usuário atual não o dispensou nas últimas 24 horas.
   */
  checkPendingContracts(): void {
    // Não exibe o alerta para usuários ROOT (evita popups repetidos ao trocar de clínica)
    if (this.authService.getPermission() === 'ROOT') {
      return;
    }

    // Primeiro verifica se deve mostrar o alerta para este usuário
    if (!this.shouldShowPendingContractsWarning()) {
      return;
    }

    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() - 3);

    // Data inicial: início dos tempos (usamos uma data bem antiga)
    const startDate = '2020-01-01';

    this.documentsService.getDocumentsWithFilters(
      '', 
      0, 
      999, 
      'PENDING_PATIENT_SIGNATURE', 
      startDate, 
      this.formatDate(endDate), 
      this.modulesService.moduleId
    ).then(response => {
      if (response.total_elements > 0) {
        this.pendingContracts = response.total_elements;
        this.showPendingContractsPopup = true;
      }
    }).catch(error => {
      console.error('Erro ao verificar contratos pendentes:', error);
    });
  }

  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  closePendingContractsPopup(): void {
    // Salva que o usuário dispensou o alerta (só funciona se o checkbox estiver marcado)
    if (this.agreeChecked) {
      this.savePendingContractsWarningDismissed();
    }
    this.showPendingContractsPopup = false;
  }

  navigateToSignatures(): void {
    this.router.navigate(['main', 'v2', 'signatures'], {
      queryParams: {
        fromWarning: 'true'
      }
    });
  }
}
